import React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";

import { isHttpStatus } from "@workspace/base-api/fetchJson";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";

import { devtoolsApi } from "./api";
import type { DeadLetterFilters } from "./dead-letter";
import { DeadLetterPanelContent } from "./dead-letter";

export function DeadLetterDevtoolPanel() {
	const [filters, setFilters] = React.useState<DeadLetterFilters>({
		limit: 10,
	});

	const { data, error, fetchNextPage, hasNextPage, status, refetch } =
		useInfiniteQuery({
			...devtoolsApi.getPagedDeadLettersOptions(filters),
			initialPageParam: { pageNumber: 1 },
			getNextPageParam: (lastPage) =>
				lastPage.nextPage ? { pageNumber: lastPage.nextPage } : undefined,
		});

	const allMessages = data?.pages.flatMap((page) => page.messages) ?? [];

	const messages = Array.from(
		new Map(allMessages.map((message) => [message.id, message])).values(),
	);

	const lastCalledAt = data?.pages[data.pages.length - 1]?.lastCalledAt;

	const handleFiltersChange = async (newFilters: DeadLetterFilters) => {
		setFilters(newFilters);
		await refetch();
	};

	if (status === "pending") return null;

	// HA-879 / HA-855 AC3 — dead letters are the platform's: anyone else is told so, not shown an error loop.
	if (isHttpStatus(error, 403)) {
		return (
			<div className="p-4">
				<Card className="w-full">
					<CardHeader>
						<CardTitle>Platform admins only</CardTitle>
						<CardDescription>
							Failed messages are for platform admins; your account can't see
							them.
						</CardDescription>
					</CardHeader>
					<CardContent />
				</Card>
			</div>
		);
	}

	if (status === "error") {
		return (
			<div className="p-4">
				<Card className="w-full">
					<CardHeader>
						<CardTitle>Couldn't load the failed messages</CardTitle>
						<CardDescription>
							{error instanceof Error ? error.message : "Something went wrong."}
						</CardDescription>
					</CardHeader>
					<CardContent>
						<Button variant="link" onClick={() => void refetch()}>
							Try again
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	return (
		<>
			<DeadLetterPanelContent
				messages={messages}
				lastCalledAt={lastCalledAt}
				hasNextPage={hasNextPage}
				onNextPage={() => fetchNextPage()}
				filters={filters}
				onFiltersChange={handleFiltersChange}
				onRefetch={async () => {
					await refetch();
				}}
			/>
		</>
	);
}
